/**
 * Presentation formatting.
 *
 * PURELY DISPLAY. Nothing here decides a value -- it only decides how an
 * already-authoritative value is written down. Rounding happens at this boundary and
 * never before: the server's numbers reach the components unmodified.
 */

/** Cents to a currency string. Money arrives as integer cents and is only divided here. */
export function formatCents(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Cents to a currency string without trailing zeros on whole amounts. */
export function formatCentsCompact(cents: number, currency = 'USD'): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function formatHashrate(hashrateTh: number): string {
  // A formatter must never be the thing that takes the page down. If the value is
  // missing or nonsense -- which in practice means the server sent a shape this build
  // did not expect -- say so in place of a number rather than throwing from render.
  // The caller is responsible for noticing; see Configure's config validation.
  if (typeof hashrateTh !== 'number' || !Number.isFinite(hashrateTh)) return '—';

  if (hashrateTh >= 1000 && hashrateTh % 1000 === 0) {
    return `${hashrateTh / 1000} PH/s`;
  }
  return `${Number(hashrateTh.toFixed(4))} TH/s`;
}

export function formatTermDays(termDays: number): string {
  return `${termDays} day${termDays === 1 ? '' : 's'}`;
}

/**
 * A probability as a percentage string.
 *
 * THE IMPORTANT CASE: solo-mining probabilities are genuinely tiny, and naively rounding
 * to two decimals turns a real chance into "0.00%", which is a lie. Anything that would
 * round away is written as "less than" the smallest figure this format can express, and
 * only an exactly-zero probability is ever shown as zero.
 */
export function formatProbabilityPercent(probability: number, maximumFractionDigits = 4): string {
  if (!Number.isFinite(probability) || probability <= 0) return '0%';

  const percent = probability * 100;
  const smallest = 10 ** -maximumFractionDigits;

  if (percent < smallest) {
    return `< ${smallest.toFixed(maximumFractionDigits)}%`;
  }

  return `${percent.toFixed(maximumFractionDigits).replace(/\.?0+$/, '')}%`;
}

/**
 * The "1 in N" framing.
 *
 * Returns null when the server reported no finite chance, so the caller can omit the
 * line rather than print "1 in Infinity".
 */
export function formatOneInChance(oneInChance: number | null | undefined): string | null {
  if (oneInChance == null || !Number.isFinite(oneInChance) || oneInChance <= 0) return null;

  const rounded = Math.round(oneInChance);
  return `1 in ${new Intl.NumberFormat('en-US').format(rounded)}`;
}

/** Expected blocks, kept readable at both ends of the range. */
export function formatExpectedBlocks(expectedBlocks: number): string {
  if (!Number.isFinite(expectedBlocks) || expectedBlocks <= 0) return '0';
  if (expectedBlocks < 0.0001) return expectedBlocks.toExponential(2);
  return Number(expectedBlocks.toPrecision(3)).toString();
}

/** A rough "how long ago" for a difficulty reading. */
export function formatAge(isoTimestamp: string, now: Date = new Date()): string | null {
  const then = new Date(isoTimestamp);
  if (Number.isNaN(then.getTime())) return null;

  const seconds = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (seconds < 90) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} hours ago`;

  return `${Math.round(hours / 24)} days ago`;
}

/**
 * A savings percentage, as the storefront states it.
 *
 * ONE FORMATTER FOR EVERY PLACE A SAVING APPEARS — the power presets and the duration
 * options — so the two can never disagree about how the same basis points read. It
 * rounds for display and does nothing else: the figure itself is calculated server-side
 * from the authoritative prices.
 *
 * The baseline configuration saves nothing against itself, and says so plainly rather
 * than claiming "Save 0%".
 */
export function formatSavingsBasisPoints(basisPoints: number | undefined | null): string {
  if (typeof basisPoints !== 'number' || !Number.isFinite(basisPoints) || basisPoints <= 0) {
    return 'Base rate';
  }
  return `Save ${Math.round(basisPoints / 100)}%`;
}
